'use client';

import Image from '../image';
import { useState } from 'react';
import { useVideo } from 'react-use';
import { Dialog } from 'tdesign-react';
import Link from 'next/link';
import playPng from './play.svg';
import { CosImage } from '../cos-image';

const PLAY_ICON_PATH = 'web-ui/video-player/play.svg';

interface IVideoPlayerProps {
  className?: string;
  imageUrl?: string;
  videoUrl?: string;
  mode: 1 | 2 | 3; // 1图片， 2 视频 3图片加链接,
  width: number;
  height: number;
  linkHref?: string;
  modalVideoUrl?: string;
  tipClassName?: string;
}

const InnerModalVideo = ({
  url,
  visible,
}: {
  url: string;
  visible: boolean;
}) => {
  const [weAppVideo, weAppVideoState, weAppVideoControls] = useVideo(
    <video
      className={`w-full h-full object-fill aspect-[16/9]`}
      controls
      loop
      controlsList="nodownload noremoteplayback noplaybackrate"
      disablePictureInPicture
      disableRemotePlayback
      preload="metadata"
      src={url}
      autoPlay={visible}
      muted={false}
    />
  );
  return weAppVideo;
};
export const ModalVideo = ({
  url,
  visible,
  close,
}: {
  url: string;
  visible: boolean;
  close: () => void;
}) => {
  const [weAppVideo, weAppVideoState, weAppVideoControls] = useVideo(
    <video
      className={`w-full h-full object-fill aspect-[16/9]`}
      controls
      loop
      controlsList="nodownload noremoteplayback noplaybackrate"
      disablePictureInPicture
      disableRemotePlayback
      preload="metadata"
      src={url}
      autoPlay={visible}
      muted={false}
    />
  );
  return (
    <>
      <Dialog
        width={'80%'}
        visible={visible}
        closeBtn
        closeOnEscKeydown
        closeOnOverlayClick
        mode="modal"
        placement="top"
        preventScrollThrough
        showOverlay
        footer={null}
        theme="default"
        onClosed={close}
        onClose={close}
      >
        {visible && <InnerModalVideo url={url} visible={visible} />}
      </Dialog>
    </>
  );
};

export const NewVideoPlayer = ({
  className,
  url,
  playing = false,
  poster,
  width,
  height,
  modalVideoUrl,
  tipClassName,
}: {
  className: string;
  url: string;
  playing: boolean;
  poster?: string;
  width: number;
  height: number;
  modalVideoUrl?: string;
  tipClassName?: string;
}) => {
  const [visible, setVisible] = useState(false);
  const onPlay = () => {
    weAppVideoControls.play();
  };
  const onPause = () => {
    weAppVideoControls.pause();
  };

  const onPreview = () => {
    weAppVideoControls.pause();
    open();
  };

  const open = () => {
    setVisible(true);
  };

  const close = () => {
    setVisible(false);
  };

  const [weAppVideo, weAppVideoState, weAppVideoControls] = useVideo(
    <video
      onMouseOver={onPlay}
      onMouseLeave={onPause}
      onClick={onPreview}
      className={`w-full h-full object-fill ${className || 'rounded-2xl'}`}
      controls={false}
      loop
      controlsList="nodownload noremoteplayback noplaybackrate"
      disablePictureInPicture
      disableRemotePlayback
      preload="metadata"
      src={url}
      muted={true}
    />
  );

  return (
    <div className={`relative w-full h-full ${className || 'rounded-2xl'}`}>
      {weAppVideo}
      <div
        className={`w-full flex justify-center py-2 bg-black/60 absolute bottom-0 ${
          tipClassName || 'rounded-br-2xl'
        } cursor-pointer`}
        onClick={open}
        onMouseOver={onPlay}
        onMouseLeave={onPause}
      >
        <span className="text-sm text-white">点击了解详情</span>
      </div>
      {poster && !weAppVideoState.playing && (
        <Image
          alt="视频"
          src={poster}
          width={width}
          height={height}
          onMouseOver={onPlay}
          className={`w-full h-full absolute left-0 top-0  ${
            className || 'rounded-2xl'
          }`}
        />
      )}
      {!weAppVideoState.playing && (
        <div
          className="absolute cursor-pointer"
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
          onClick={onPlay}
        >
          <CosImage
            fallbackSrc={playPng}
            path={PLAY_ICON_PATH}
            className="w-10 h-10"
            alt="播放按钮"
          />
        </div>
      )}

      <ModalVideo url={modalVideoUrl || url} visible={visible} close={close} />
    </div>
  );
};

export const VideoPlayer: React.FC<IVideoPlayerProps> = ({
  className,
  imageUrl,
  videoUrl,
  mode,
  width,
  height,
  linkHref,
  modalVideoUrl,
  tipClassName,
}) => {
  return (
    <div
      className={`relative w-full h-full flex-1 ${className || 'rounded-2xl'}`}
    >
      {mode === 2 && (
        <NewVideoPlayer
          playing={true}
          className={`object-fill ${className || 'rounded-2xl'}`}
          url={videoUrl || ''}
          modalVideoUrl={modalVideoUrl}
          poster={imageUrl}
          width={width}
          height={height}
          tipClassName={tipClassName}
        />
      )}
      {imageUrl && mode === 1 && (
        <Image
          alt="视频"
          src={imageUrl}
          width={width}
          height={height}
          className={`w-full h-full absolute left-0 top-0  ${
            className || 'rounded-2xl'
          }`}
        />
      )}
      {imageUrl && mode === 3 && (
        <Link href={linkHref || ''} target="_blank">
          <Image
            alt="视频"
            src={imageUrl}
            width={width}
            height={height}
            className={`w-full h-full absolute left-0 top-0  ${
              className || 'rounded-2xl'
            }`}
          />
          <div
            className={`w-full flex justify-center py-2 bg-black/60 absolute bottom-0 ${
              tipClassName || 'rounded-b-2xl'
            } cursor-pointer`}
          >
            <span className="text-sm text-white">了解详情</span>
          </div>
        </Link>
      )}
    </div>
  );
};
